import { useState, useEffect } from "react";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";

interface ReverseGeocodeResult {
  address: string;
  fullAddress: {
    route: string;
    streetNumber: string;
    sublocality: string;
    locality: string;
    state: string;
    postalCode: string;
    country: string;
    formatted: string;
  } | null;
  isLoading: boolean;
}

export const useReverseGeocode = (
  coordinates: [number, number] | null,
  fallbackAddress?: string,
): ReverseGeocodeResult => {
  const [address, setAddress] = useState(fallbackAddress || "");
  const [fullAddress, setFullAddress] = useState<ReverseGeocodeResult["fullAddress"]>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (fallbackAddress) {
      setAddress(fallbackAddress);
      return;
    }

    if (
      !coordinates ||
      coordinates.length !== 2 ||
      isNaN(coordinates[0]) ||
      isNaN(coordinates[1])
    ) {
      return;
    }

    setIsLoading(true);

    loadGoogleMaps(() => {
      if (!window.google?.maps) {
        setIsLoading(false);
        return;
      }

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        { location: { lat: coordinates[0], lng: coordinates[1] } },
        (results: any, status: string) => {
          setIsLoading(false);

          if (status !== "OK" || !results?.[0]) return;

          const result = results[0];
          const components = result.address_components;

          const get = (type: string) =>
            components.find((c: any) => c.types.includes(type))?.long_name || "";

          const route = get("route");
          const streetNumber = get("street_number");
          const sublocality = get("sublocality_level_1") || get("sublocality");
          const locality = get("locality") || get("administrative_area_level_2");
          const state = get("administrative_area_level_1");
          const postalCode = get("postal_code");
          const country = get("country");

          const parts = [];
          if (route) parts.push(streetNumber ? `${route}, ${streetNumber}` : route);
          const locationParts = [sublocality, locality, state].filter(Boolean);
          if (locationParts.length) parts.push(locationParts.join(", "));
          if (postalCode) parts.push(postalCode);
          if (country) parts.push(country);

          const formatted = parts.length > 0 ? parts.join(", ") : result.formatted_address;

          setAddress(formatted);
          setFullAddress({
            route,
            streetNumber,
            sublocality,
            locality,
            state,
            postalCode,
            country,
            formatted: result.formatted_address,
          });
        },
      );
    });
  }, [coordinates?.[0], coordinates?.[1], fallbackAddress]);

  return { address, fullAddress, isLoading };
};
